/**
 * UnifiedPatientTable - Primary Patient/Appointment Table
 *
 * Main table displaying all unified records with:
 * - Priority-based sorting (highest urgency first)
 * - SLA color-coded row backgrounds
 * - Expandable rows for journey timeline
 * - Column filters and search
 * - Call type indicators (voice/video)
 * - "No MR Record" badge for call-only records
 */

import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  Table,
  Tag,
  Typography,
  Space,
  Badge,
  Tooltip,
  Drawer,
  Progress,
} from 'antd';
import {
  PhoneOutlined,
  VideoCameraOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useUnifiedDashboardStore } from '../../store';
import PatientRowExpanded from './PatientRowExpanded';
import ResizeHandle from './ResizeHandle';
import useTableResize from '../../hooks/useTableResize';
import { parseFieldSetup } from '../../utils/stringUtils';

dayjs.extend(utc);

const { Text } = Typography;

// Consistent color for all multi-patient calls
const MULTI_PATIENT_COLOR = '#1890ff'; // blue

// Statuses excluded from default filter
const EXCLUDED_DEFAULT_STATUSES = [
  'completed',
  'cancelled',
  'wrapping',
  'at billing',
];

const UnifiedPatientTable = () => {
  const { unifiedRecords, isFetching, isWaitingRoomTableVisible } =
    useUnifiedDashboardStore();

  // Process records to add multi-patient group info
  const processedRecords = useMemo(() => {
    // Group records by taskSid for multi-patient calls
    const taskSidGroups = new Map();

    unifiedRecords.forEach((record) => {
      if (record.call?.isMultiPatient && record.call?.taskSid) {
        const taskSid = record.call.taskSid;
        if (!taskSidGroups.has(taskSid)) {
          taskSidGroups.set(taskSid, []);
        }
        taskSidGroups.get(taskSid).push(record.id);
      }
    });

    // Add position info to each record
    return unifiedRecords.map((record) => {
      if (record.call?.isMultiPatient && record.call?.taskSid) {
        const taskSid = record.call.taskSid;
        const group = taskSidGroups.get(taskSid) || [];
        const position = group.indexOf(record.id) + 1;
        const total = group.length;
        const isFirst = position === 1;
        const isLast = position === total;

        return {
          ...record,
          _multiPatient: {
            position,
            total,
            isFirst,
            isLast,
            groupId: taskSid.slice(-6),
            color: MULTI_PATIENT_COLOR,
          },
        };
      }
      return record;
    });
  }, [unifiedRecords]);

  const [selectedRecord, setSelectedRecord] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Resizable table height via drag handle (abstracted hook)
  const { customTableHeight, tableWrapperRef, handleResizeMouseDown } =
    useTableResize();

  // Build filter options from data
  const buildFilters = useCallback(
    (accessor) => {
      const values = new Set();
      unifiedRecords.forEach((record) => {
        const val = accessor(record);
        if (val && typeof val === 'string' && val.trim()) {
          values.add(val.trim());
        }
      });
      return Array.from(values)
        .sort()
        .map((v) => ({ text: v, value: v.toLowerCase() }));
    },
    [unifiedRecords]
  );

  const siteFilters = useMemo(
    () => buildFilters((r) => r.site?.code),
    [buildFilters]
  );

  const statusFilters = useMemo(
    () => buildFilters((r) => r.overallStatus),
    [buildFilters]
  );

  // Controlled status filter: default to all except Completed/Cancelled
  const [statusFilteredValue, setStatusFilteredValue] = useState(null);
  const statusFilterInitialized = useRef(false);
  useEffect(() => {
    if (!statusFilterInitialized.current && statusFilters.length > 0) {
      statusFilterInitialized.current = true;
      setStatusFilteredValue(
        statusFilters
          .map((f) => f.value)
          .filter((v) => !EXCLUDED_DEFAULT_STATUSES.includes(v))
      );
    }
  }, [statusFilters]);

  const clientFilters = useMemo(
    () => buildFilters((r) => r.site?.client),
    [buildFilters]
  );

  // Get status icon
  const getStatusIcon = (record) => {
    if (record.slaStatus?.percentage >= 100) {
      return <WarningOutlined style={{ color: '#ff4d4f' }} />;
    }
    if (record.hasActiveCall) {
      return <Badge status="processing" />;
    }
    if (record.overallStatus === 'Completed') {
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    }
    if (record.overallStatus?.includes('Waiting')) {
      return <ClockCircleOutlined style={{ color: '#faad14' }} />;
    }
    return null;
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        title: '',
        key: 'statusIcon',
        width: 40,
        align: 'center',
        render: (_, record) => getStatusIcon(record),
      },
      {
        title: 'Patient',
        key: 'patient',
        width: 180,
        fixed: 'left',
        render: (_, record) => {
          const firstName = parseFieldSetup(record.patient?.firstName || '');
          const lastName = parseFieldSetup(record.patient?.lastName || '');
          const name = `${firstName} ${lastName}`.trim();
          const mp = record._multiPatient;

          return (
            <Space direction="vertical" size={0}>
              <Text strong ellipsis style={{ maxWidth: 170 }}>
                {name || 'Unknown Patient'}
              </Text>
              <Space size={4}>
                {!record.hasRealtimeData && record.hasActiveCall && (
                  <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>
                    Call Only
                  </Tag>
                )}
                {mp && (
                  <Tooltip
                    title={`Multi-patient call #${mp.groupId} - Patient ${mp.position} of ${mp.total}`}
                    color={mp.color}
                  >
                    <Tag
                      icon={<UsergroupAddOutlined />}
                      style={{
                        fontSize: 10,
                        margin: 0,
                        cursor: 'help',
                        backgroundColor: mp.color,
                        borderColor: mp.color,
                        color: '#fff',
                      }}
                    >
                      {mp.position}/{mp.total}
                    </Tag>
                  </Tooltip>
                )}
              </Space>
            </Space>
          );
        },
      },
      {
        title: 'DOB',
        key: 'dob',
        width: 100,
        render: (_, record) => {
          const dob = record.patient?.dob;
          if (!dob) return '-';
          return dayjs(dob).utc().format('DD/MM/YYYY');
        },
      },
      {
        title: 'Site',
        key: 'site',
        dataIndex: ['site', 'code'],
        width: 120,
        ellipsis: true,
        filters: siteFilters,
        filterSearch: true,
        onFilter: (value, record) =>
          (record.site?.code || '').toLowerCase().includes(value),
      },
      {
        title: 'Status',
        key: 'status',
        width: 130,
        filters: statusFilters,
        filteredValue: statusFilteredValue,
        onFilter: (value, record) =>
          (record.overallStatus || '').toLowerCase().includes(value),
        render: (_, record) => {
          const status = record.overallStatus;
          let color = 'default';

          if (status === 'Completed') color = 'default';
          else if (status === 'With Agent' || status === 'With Doctor')
            color = 'blue';
          else if (status === 'In Queue') color = 'orange';
          else if (status?.includes('Waiting')) color = 'gold';
          else if (status === 'Wrapping') color = 'purple';

          return <Tag color={color}>{status}</Tag>;
        },
      },
      {
        title: 'Call',
        key: 'callType',
        width: 50,
        align: 'center',
        render: (_, record) => {
          if (!record.hasActiveCall) return '-';

          const type = record.call?.callType;
          if (type === 'voice') {
            return (
              <Tooltip title="Voice Call">
                <PhoneOutlined style={{ color: '#4eb5b0', fontSize: 16 }} />
              </Tooltip>
            );
          }
          if (type === 'video') {
            return (
              <Tooltip title="Video Call">
                <VideoCameraOutlined
                  style={{ color: '#6f86d6', fontSize: 16 }}
                />
              </Tooltip>
            );
          }
          return <Badge status="processing" />;
        },
      },
      {
        title: 'Agent',
        key: 'agent',
        width: 120,
        ellipsis: true,
        render: (_, record) => {
          const agent = record.call?.agentName;
          if (!agent) return '-';

          // Show initials or shortened name
          const parts = agent.split(' ');
          if (parts.length >= 2) {
            return (
              <Tooltip title={agent}>
                <Text>
                  {parts[0].charAt(0)}. {parts[parts.length - 1]}
                </Text>
              </Tooltip>
            );
          }
          return agent;
        },
      },
      {
        title: 'Wait (min)',
        key: 'waitTime',
        width: 85,
        align: 'center',
        render: (_, record) => {
          // Don't show wait time if task is in wrapping status
          if (record.call?.status === 'wrapping') return '-';

          const wait = record.waitingRoom?.waitTime;
          if (!Number.isFinite(wait)) return '-';

          // Color code based on wait time (same as WaitingRoomPanel)
          const bgColor =
            wait >= 30 ? '#ffe7e7' : wait >= 15 ? '#fff7e6' : '#e6ffe6';

          if (wait >= 60) {
            const hours = Math.floor(wait / 60);
            const mins = wait % 60;
            return (
              <span
                style={{
                  backgroundColor: bgColor,
                  padding: '2px 6px',
                  borderRadius: 4,
                  fontWeight: 500,
                  fontSize: 12,
                }}
              >
                {hours}h {mins}m
              </span>
            );
          }
          return (
            <span
              style={{
                backgroundColor: bgColor,
                padding: '2px 6px',
                borderRadius: 4,
                fontWeight: 500,
                fontSize: 12,
              }}
            >
              {wait}
            </span>
          );
        },
        sorter: (a, b) =>
          (a.waitingRoom?.waitTime || 0) - (b.waitingRoom?.waitTime || 0),
      },
      {
        title: 'SLA',
        key: 'sla',
        width: 100,
        render: (_, record) => {
          const { slaStatus, waitingRoom } = record;
          if (
            !slaStatus?.percentage ||
            !Number.isFinite(waitingRoom?.waitTime)
          ) {
            return <Text type="secondary">-</Text>;
          }

          const waitTime = waitingRoom.waitTime;
          const slaMinutes = waitingRoom.slaMinutes;
          const remaining = slaMinutes - waitTime;
          const isOver = remaining < 0;
          const absRemaining = Math.abs(remaining);

          // Format time strings
          const formatTime = (mins) => {
            if (mins >= 60) {
              return `${Math.floor(mins / 60)}h${mins % 60}m`;
            }
            return `${mins}m`;
          };

          const remainingStr = isOver
            ? `+${formatTime(absRemaining)}`
            : formatTime(absRemaining);

          // Get progress bar color
          const getProgressColor = (pct) => {
            if (pct >= 100) return '#ff4d4f';
            if (pct >= 80) return '#fa8c16';
            if (pct >= 50) return '#d4b106';
            return '#52c41a';
          };

          return (
            <Tooltip title={`SLA: ${slaMinutes}min, Wait: ${waitTime}min`}>
              <div style={{ lineHeight: 1.2 }}>
                <Progress
                  percent={Math.min(slaStatus.percentage, 100)}
                  size="small"
                  strokeColor={getProgressColor(slaStatus.percentage)}
                  style={{ width: '100%', marginBottom: 2 }}
                  showInfo={false}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 10,
                  }}
                >
                  <span style={{ color: '#8c8c8c' }}>
                    {formatTime(waitTime)}
                  </span>
                  <span style={{ color: '#d9d9d9' }}>|</span>
                  <span style={{ color: isOver ? '#ff4d4f' : '#52c41a' }}>
                    {remainingStr}
                  </span>
                </div>
              </div>
            </Tooltip>
          );
        },
        sorter: (a, b) =>
          (b.slaStatus?.percentage || 0) - (a.slaStatus?.percentage || 0),
      },
      {
        title: 'Triage',
        key: 'triage',
        width: 55,
        align: 'center',
        render: (_, record) => {
          const triage = record.appointment?.triageCategoryNum;
          if (!triage) return '-';

          const colors = {
            1: 'red',
            2: 'orange',
            3: 'gold',
            4: 'blue',
            5: 'default',
          };

          return (
            <Tag color={colors[triage] || 'default'} size="small">
              {triage}
            </Tag>
          );
        },
        sorter: (a, b) =>
          (a.appointment?.triageCategoryNum || 99) -
          (b.appointment?.triageCategoryNum || 99),
      },
      {
        title: 'Client',
        key: 'client',
        dataIndex: ['site', 'client'],
        width: 100,
        ellipsis: true,
        filters: clientFilters,
        filterSearch: true,
        onFilter: (value, record) =>
          (record.site?.client || '').toLowerCase().includes(value),
      },
    ],
    [siteFilters, statusFilters, clientFilters, statusFilteredValue]
  );

  // Row styling based on SLA (SLA is the priority, multi-patient uses border only)
  const getRowStyle = (record) => {
    const mp = record._multiPatient;

    const style = {
      backgroundColor: record.rowColor || 'transparent', // SLA color is always priority
      cursor: 'pointer',
    };

    // Set CSS variable for the multi-patient border color (for left border indicator)
    if (mp) {
      style['--mp-color'] = mp.color;
    }

    return style;
  };

  // Row class name for multi-patient grouping
  const getRowClassName = (record) => {
    const mp = record._multiPatient;
    if (mp) {
      const classes = ['multi-patient-row'];
      if (mp.isFirst) classes.push('multi-patient-first');
      if (mp.isLast) classes.push('multi-patient-last');
      return classes.join(' ');
    }
    return '';
  };

  // Calculate table scroll height based on waiting room table visibility
  // When user has manually resized, use that value; otherwise compute from viewport
  const tableScrollY = customTableHeight
    ? customTableHeight
    : Math.max(
        isWaitingRoomTableVisible ? 400 : 500,
        window.innerHeight - (isWaitingRoomTableVisible ? 550 : 200)
      );

  // Handle row click - open drawer with patient details
  const handleRowClick = (record) => {
    setSelectedRecord(record);
    setDrawerOpen(true);
  };

  // Close drawer
  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <div className="unified-patient-table-wrapper" ref={tableWrapperRef}>
      {/* Main Table - no blocking loading indicator, refresh happens in background */}
      <Table
        bordered
        size="small"
        dataSource={processedRecords}
        columns={columns}
        rowKey="id"
        loading={isFetching && processedRecords.length === 0}
        locale={{
          emptyText: isFetching ? 'Loading data...' : 'No records found',
        }}
        scroll={{ x: 1100, y: tableScrollY }}
        pagination={{
          pageSize: 50,
          size: 'small',
          position: ['bottomRight'],
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          showTotal: (total) => `${total} records`,
        }}
        rowClassName={getRowClassName}
        onRow={(record) => ({
          style: getRowStyle(record),
          onClick: () => handleRowClick(record),
        })}
        onChange={(_pagination, filters) => {
          setStatusFilteredValue(filters.status || null);
        }}
      />

      <ResizeHandle onMouseDown={handleResizeMouseDown} />

      {/* Patient Details Drawer */}
      <Drawer
        title={
          selectedRecord
            ? `${selectedRecord.patient?.firstName || ''} ${selectedRecord.patient?.lastName || ''}`.trim() ||
              'Patient Details'
            : 'Patient Details'
        }
        placement="right"
        width={400}
        open={drawerOpen}
        onClose={handleDrawerClose}
        styles={{
          body: { padding: 0 },
          header: { padding: '12px 16px' },
        }}
      >
        {selectedRecord && <PatientRowExpanded record={selectedRecord} />}
      </Drawer>

      {/* Custom CSS for row colors and compact rows */}
      <style>{`
        /* Compact row height - only for main unified patient table */
        .unified-patient-table-wrapper .ant-table.ant-table-small .ant-table-tbody > tr > td {
          padding: 4px 8px !important;
        }
        .unified-patient-table-wrapper .ant-table.ant-table-small .ant-table-thead > tr > th {
          padding: 6px 8px !important;
        }
        .unified-patient-table-wrapper .ant-table-cell {
          padding: 4px 8px !important;
        }
        .unified-patient-table-wrapper .sla-breached-row {
          background-color: #ffebee !important;
        }
        .unified-patient-table-wrapper .sla-critical-row {
          background-color: #fff3e0 !important;
        }
        .unified-patient-table-wrapper .sla-warning-row {
          background-color: #fffde7 !important;
        }
        .unified-patient-table-wrapper .completed-row {
          background-color: #fafafa !important;
        }
        .unified-patient-table-wrapper .ant-table-row:hover > td {
          background-color: inherit !important;
          filter: brightness(0.97);
        }
        /* Multi-patient row styling - connected group visual */
        .unified-patient-table-wrapper .multi-patient-row {
          position: relative;
        }
        .unified-patient-table-wrapper .multi-patient-row > td:first-child::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: var(--mp-color, #1890ff);
        }
        .unified-patient-table-wrapper .multi-patient-first > td:first-child::before {
          top: 50%;
          border-radius: 2px 2px 0 0;
        }
        .unified-patient-table-wrapper .multi-patient-last > td:first-child::before {
          bottom: 50%;
          border-radius: 0 0 2px 2px;
        }
        .unified-patient-table-wrapper .multi-patient-first.multi-patient-last > td:first-child::before {
          top: 25%;
          bottom: 25%;
          border-radius: 2px;
        }
        .unified-patient-table-wrapper .multi-patient-row:hover {
          filter: brightness(0.98);
        }
        /* Resize handle styles are in ResizeHandle.jsx */
      `}</style>
    </div>
  );
};

export default UnifiedPatientTable;
